import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Users, Search, Phone, Mail, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomersManager = ({ token, language }) => {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchCustomers = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/customers`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setData(await r.json());
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const openCustomer = async (c) => {
    setSelected(c);
    setDetail(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/customers/${c.user_id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) setDetail(await r.json());
    } catch (e) { console.error(e); }
  };

  const toggleActive = async (c, value) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/customers/${c.user_id}`, {
        method: 'PUT', headers, body: JSON.stringify({ is_active: value }),
      });
      if (r.ok) {
        toast.success(value
          ? (language === 'pt' ? 'Conta ativada' : 'Account activated')
          : (language === 'pt' ? 'Conta bloqueada' : 'Account blocked'));
        setSelected({ ...c, is_active: value });
        fetchCustomers();
      }
    } catch (e) { toast.error('Erro'); }
  };

  const list = (data?.customers || []).filter(c => {
    const q = search.toLowerCase();
    return !q || (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
  });

  return (
    <div className="space-y-4">
      {/* Counters */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Clientes na Plataforma' : 'Platform Customers'}</p>
            <p className="text-2xl font-bold text-[#004E64]" data-testid="platform-total-customers">{data?.total_customers ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Contas Ativas' : 'Active Accounts'}</p>
            <p className="text-2xl font-bold text-[#2EC4B6]" data-testid="platform-active-accounts">{data?.active_accounts ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[#9FA2B4] text-xs">{language === 'pt' ? 'Ativos (30 dias)' : 'Active (30 days)'}</p>
            <p className="text-2xl font-bold text-[#FF6B35]" data-testid="platform-active-30d">{data?.active_30d ?? '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Customer list */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users size={16} className="text-[#2EC4B6]" />
            {language === 'pt' ? 'Todos os Clientes' : 'All Customers'}
          </CardTitle>
          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9FA2B4]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'pt' ? 'Procurar por nome, email ou telefone...' : 'Search by name, email or phone...'}
              className="pl-9 rounded-full"
              data-testid="customer-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {list.map(c => (
              <button
                key={c.user_id}
                onClick={() => openCustomer(c)}
                className="w-full flex items-center justify-between p-2 bg-[#F4F1EA] rounded-xl text-left hover:ring-2 hover:ring-[#2EC4B6]/40 transition-shadow"
                data-testid={`customer-row-${c.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#004E64] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {(c.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#004E64] flex items-center gap-2">
                      {c.name}
                      {c.is_active === false && <Badge className="bg-red-500 hover:bg-red-500 text-[10px]">{language === 'pt' ? 'Bloqueado' : 'Blocked'}</Badge>}
                      {c.recently_active && c.is_active !== false && <span className="w-2 h-2 rounded-full bg-green-500" title={language === 'pt' ? 'Ativo nos últimos 30 dias' : 'Active last 30 days'} />}
                    </div>
                    <p className="text-xs text-[#9FA2B4]">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-[#FF6B35]">€{(c.total_spent || 0).toFixed(2)}</p>
                  <p className="text-xs text-[#9FA2B4]">{c.orders} {language === 'pt' ? 'pedidos' : 'orders'}</p>
                </div>
              </button>
            ))}
            {!list.length && <p className="text-sm text-[#9FA2B4] text-center py-4">{language === 'pt' ? 'Sem clientes' : 'No customers'}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Customer detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg" data-testid="customer-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#004E64]">{selected.name}</DialogTitle>
                <DialogDescription>
                  {language === 'pt' ? 'Gestão do cliente — dados, pedidos e estado da conta' : 'Customer management — details, orders and account status'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3 text-sm text-[#004E64]">
                  <span className="flex items-center gap-1"><Mail size={14} className="text-[#9FA2B4]" /> {selected.email}</span>
                  {selected.phone && <span className="flex items-center gap-1"><Phone size={14} className="text-[#9FA2B4]" /> {selected.phone}</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-[#F4F1EA] rounded-xl p-2">
                    <p className="text-lg font-bold text-[#004E64]">{detail?.total_orders ?? selected.orders}</p>
                    <p className="text-[10px] text-[#9FA2B4]">{language === 'pt' ? 'Pedidos' : 'Orders'}</p>
                  </div>
                  <div className="bg-[#F4F1EA] rounded-xl p-2">
                    <p className="text-lg font-bold text-[#FF6B35]">€{(selected.total_spent || 0).toFixed(2)}</p>
                    <p className="text-[10px] text-[#9FA2B4]">{language === 'pt' ? 'Total Gasto' : 'Total Spent'}</p>
                  </div>
                  <div className="bg-[#F4F1EA] rounded-xl p-2">
                    <p className="text-lg font-bold text-[#2EC4B6]">{selected.last_order ? new Date(selected.last_order).toLocaleDateString('pt-PT') : '—'}</p>
                    <p className="text-[10px] text-[#9FA2B4]">{language === 'pt' ? 'Último Pedido' : 'Last Order'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-[#004E64]/10">
                  <div>
                    <p className="text-sm font-semibold text-[#004E64]">{language === 'pt' ? 'Conta ativa' : 'Active account'}</p>
                    <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Desliga para bloquear o login e pedidos deste cliente' : 'Turn off to block this customer'}</p>
                  </div>
                  <Switch
                    checked={selected.is_active !== false}
                    onCheckedChange={(v) => toggleActive(selected, v)}
                    data-testid="customer-active-toggle"
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#004E64] mb-2 flex items-center gap-1">
                    <ShoppingBag size={14} /> {language === 'pt' ? 'Últimos pedidos' : 'Recent orders'}
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {(detail?.recent_orders || []).map(o => (
                      <div key={o.order_id} className="flex items-center justify-between text-xs bg-[#F4F1EA] rounded-lg p-2">
                        <span className="font-mono">#{o.order_id.slice(-8)}</span>
                        <Badge variant="outline" className="text-[10px]">{o.status}</Badge>
                        <span className="font-bold text-[#FF6B35]">€{(o.total || 0).toFixed(2)}</span>
                        <span className="text-[#9FA2B4]">{o.created_at ? new Date(o.created_at).toLocaleDateString('pt-PT') : ''}</span>
                      </div>
                    ))}
                    {detail && !detail.recent_orders?.length && <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Sem pedidos' : 'No orders'}</p>}
                    {!detail && <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'A carregar...' : 'Loading...'}</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersManager;
